import React, { useEffect, useState, useRef } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link, useNavigate } from 'react-router-dom';
import { PasswordResetModal } from '../PasswordResetModal';
import { AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
export const StudentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const {
    login
  } = useAuth();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.removeItem('scrollToTopAfterSignIn');
  }, []);
  // Scroll to error message when error state changes
  useEffect(() => {
    if (error) {
      window.scrollTo(0, 0);
      if (errorRef.current) {
        setTimeout(() => {
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [error]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      console.log('=== STUDENT LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Calling login with type: student');
      // Use Firebase authentication
      await login(email, password, 'student');
      console.log('Login successful, navigating to dashboard');
      // Navigate to dashboard on successful login
      navigate('/student-dashboard');
    } catch (error: any) {
      console.error('=== STUDENT LOGIN ERROR ===');
      console.error('Login error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      // Handle user-actionable errors specifically
      if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later or reset your password.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7040">
      <Navbar data-id="element-7041" />
      <main className="pt-32 pb-16" data-id="element-7042">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7043">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-7044">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-7045">
              Student Login
            </h2>
            <p className="text-gray-600 text-center mb-8" data-id="element-7046">
              Sign in to access your student dashboard
            </p>
            {/* Error message at the top of the form */}
            {error && <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7047">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7048" />
                <span data-id="element-7049">{error}</span>
              </div>}
            <form ref={formRef} onSubmit={handleSubmit} data-id="element-7050">
              <div className="space-y-6" data-id="element-7051">
                {/* Email Address */}
                <div data-id="element-7052">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7053">
                    Email Address
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required autoCapitalize="off" autoComplete="email" autoCorrect="off" spellCheck="false" inputMode="email" data-id="element-7054" />
                </div>
                {/* Password */}
                <div data-id="element-7055">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7056">
                    Password
                  </label>
                  <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Enter your password" required autoComplete="current-password" autoCapitalize="off" data-id="element-7057" />
                </div>
                {/* Forgot Password */}
                <div className="text-right" data-id="element-7058">
                  <button type="button" onClick={() => setIsResetModalOpen(true)} className="text-sm text-deep-pink hover:text-burgundy" data-id="element-7059">
                    Forgot your password?
                  </button>
                </div>
                {/* Submit Button */}
                <button type="submit" disabled={isSubmitting} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center" data-id="element-7060">
                  {isSubmitting ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7061">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7062"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7063"></path>
                      </svg>
                      Signing In...
                    </> : 'Sign In'}
                </button>
                <p className="text-sm text-gray-500 text-center mt-4" data-id="element-7064">
                  Don't have an account?{' '}
                  <Link to="/student-signup" className="text-deep-pink hover:text-burgundy" data-id="element-7065">
                    Create account
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer data-id="element-7066" />
      {/* Password Reset Modal */}
      <PasswordResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} data-id="element-7067" />
    </div>;
};